type UsageData = {
  monthly_hours_usage: Record<string, number>;
  hours_limit: number;
  hours_limit_used: number;
  period_start: string;
  period_end: string;
};

export const estimateExpirationDate = (data: UsageData): string => {
  const { monthly_hours_usage = {}, hours_limit, hours_limit_used, period_end } = data;
  if (!period_end) return '';

  const remainingHours = Number(hours_limit) - Number(hours_limit_used);
  if (!isFinite(remainingHours) || remainingHours <= 0) return '';

  const usageValues = Object.values(monthly_hours_usage).map(Number);
  if (usageValues.length > 1) usageValues.pop();

  const avg = usageValues.length > 0 ? usageValues.reduce((s, h) => s + (isFinite(h) ? h : 0), 0) / usageValues.length : 30;

  const averageUsage = Math.max(0.1, avg); // avoid divide-by-zero
  const monthsRemaining = Math.ceil(remainingHours / averageUsage);
  if (!isFinite(monthsRemaining) || monthsRemaining < 0) return '';

  const estimatedDateObj = new Date();
  estimatedDateObj.setMonth(estimatedDateObj.getMonth() + monthsRemaining);
  if (isNaN(estimatedDateObj.getTime())) return '';

  const estimatedDate = estimatedDateObj.toISOString().slice(0, 7);

  const [yearStr, monthStr] = period_end.split('-');
  const periodEndDate = new Date(Number(yearStr), Number(monthStr), 0);
  const diffDays = (estimatedDateObj.getTime() - periodEndDate.getTime()) / 86400000;

  return diffDays > 30 ? '' : estimatedDate;
};

export const estimateTotalUsage = (data: UsageData): number => {
  const { monthly_hours_usage = {}, hours_limit_used = 0, period_end } = data;

  // Get all monthly usage values and exclude the last entry if there are multiple
  const usageValues = Object.values(monthly_hours_usage);
  if (usageValues.length > 1) usageValues.pop();

  // Fallback to 30 if no past data
  const averageUsage = usageValues.length > 0 ? usageValues.reduce((sum, hours) => sum + hours, 0) / usageValues.length : 0;

  const currentDate = new Date();
  const periodEndDate = new Date(period_end);

  // Calculate number of full months remaining
  let monthsRemaining =
    (periodEndDate.getFullYear() - currentDate.getFullYear()) * 12 + (periodEndDate.getMonth() - currentDate.getMonth());

  monthsRemaining = Math.max(0, monthsRemaining);

  const projectedAdditionalUsage = monthsRemaining * averageUsage;

  // Ensure hours_limit_used is a number
  const used = Number(hours_limit_used) || 0;

  return Math.max(used + projectedAdditionalUsage);
};
